import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { MOCK_AUTH_ENABLED } from '../lib/constants';
import { loginRequest, type UserProfile } from '../lib/auth';
import { setAuthTokenProvider } from '../lib/api';

// Lazy-load mock auth — never bundled in production builds
async function loadMock() {
  const m = await import('../lib/mock-auth');
  return { user: m.MOCK_USER, token: m.getMockToken };
}

interface AuthContextValue {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string>;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  getToken: async () => '',
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mock auth for development / QA
  useEffect(() => {
    if (MOCK_AUTH_ENABLED) {
      loadMock().then(({ user, token }) => {
        setUser(user);
        setIsLoading(false);
        setAuthTokenProvider(async () => token());
      });
      return;
    }

    if (inProgress === InteractionStatus.None) {
      if (isMsalAuthenticated && accounts.length > 0) {
        const account = accounts[0];
        const claims = account.idTokenClaims as Record<string, unknown>;
        const memberships = (claims?.tenant_memberships as UserProfile['tenantMemberships']) || [];

        setUser({
          id: account.localAccountId,
          email: account.username,
          name: account.name || account.username,
          isPlatformAdmin: claims?.is_platform_admin === true,
          tenantMemberships: memberships,
        });

        setAuthTokenProvider(async () => {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          return response.accessToken;
        });
      } else {
        setUser(null);
      }
      setIsLoading(false);
    }
  }, [isMsalAuthenticated, accounts, inProgress, instance]);

  const login = useCallback(async () => {
    if (MOCK_AUTH_ENABLED) {
      const { user } = await loadMock();
      setUser(user);
      return;
    }
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    if (MOCK_AUTH_ENABLED) {
      setUser(null);
      return;
    }
    await instance.logoutRedirect();
  }, [instance]);

  const getToken = useCallback(async (): Promise<string> => {
    if (MOCK_AUTH_ENABLED) {
      const { token } = await loadMock();
      return token();
    }
    const account = accounts[0];
    if (!account) throw new Error('No authenticated account');
    const response = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return response.accessToken;
  }, [instance, accounts]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      logout,
      getToken,
    }),
    [user, isLoading, login, logout, getToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
